/*
 * Find My Routine quiz.
 * - Suggestions come from the section's Recommendation blocks (real products, live price/stock).
 * - Answers are kept in this browser. "Save my routine" builds a link (answers in the URL) that
 *   reopens the same results on any device. With "Shopify signup" on, the email form saves her as a customer tagged
 *   with her answers (Shopify double opt-in email carries the routine); otherwise email/text open her own app.
 * - "Review and add" hands the picks to the My Routine planner (my-routine:apply), which owns
 *   variants, totals, Already have / Skip, set overlap and adding to the cart.
 */
(() => {
  if (customElements.get('routine-quiz')) return;

  const STORAGE_KEY = 'naturall-quiz-v1';

  /* Quiz measurement: custom customer events (quiz_started, quiz_completed, quiz_email_submitted,
     quiz_routine_review) for any connected pixel, plus a "Quiz" cart attribute so orders from
     shoppers who finished the quiz show it in the order's Additional details. */
  const track = (name, data) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, data || {});
      }
    } catch (e) { /* ignore */ }
  };
  const markCart = (value) => {
    try {
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { Quiz: value } })
      }).catch(() => {});
    } catch (e) { /* ignore */ }
  };
  const JOBS = ['cleanse', 'condition', 'moisturize', 'style', 'oil', 'edge'];
  const QUESTIONS = ['q1', 'q2', 'q3', 'q4'];

  function esc(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    })[c]);
  }

  class RoutineQuiz extends HTMLElement {
    connectedCallback() {
      this.d = this.dataset;
      try {
        this.recs = JSON.parse(this.querySelector('[data-quiz-recs]').textContent) || [];
      } catch (e) {
        this.recs = [];
      }
      this.recs.forEach((r) => { r.answers = (r.answers || []).map((a) => a.trim()).filter(Boolean); });
      this.stepLabels = (this.d.stepLabels || '').split(',').map((s) => s.trim());
      this.browseLinks = (this.d.browseLinks || '').split(',').map((s) => s.trim());
      this.form = this.querySelector('[data-quiz-form]');
      this.intro = this.querySelector('[data-quiz-intro]');
      this.questions = Array.from(this.querySelectorAll('[data-question]'));
      this.progress = this.querySelector('[data-quiz-progress]');
      this.error = this.querySelector('[data-quiz-error]');
      this.back = this.querySelector('[data-quiz-back]');
      this.next = this.querySelector('[data-quiz-next]');
      this.results = this.querySelector('[data-quiz-results]');
      this.cards = this.querySelector('[data-quiz-cards]');
      this.index = 0;

      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.goNext(); });
      this.back.addEventListener('click', () => this.show(this.index - 1, true));
      this.querySelector('[data-quiz-restart]').addEventListener('click', () => this.restart());
      this.querySelector('[data-quiz-review]').addEventListener('click', () => this.review());
      this.form.addEventListener('change', () => { this.error.hidden = true; });

      this.setupSave();

      const fromLink = this.fromUrl();
      if (fromLink) this.save(fromLink);
      const saved = fromLink ? { answers: fromLink } : this.load();
      if (saved && saved.answers && saved.answers.q1) {
        this.restore(saved.answers);
        this.showResults(false);
      } else {
        this.show(0, false);
      }
    }

    /* Answers carried in a saved routine link (?q1=..&q2=..&q3=..&q4=a,b). Only known answer values are kept. */
    fromUrl() {
      const params = new URLSearchParams(window.location.search);
      const valid = (name, value) => !!value && !!this.form.querySelector(`input[name="${name}"][value="${CSS.escape(value)}"]`);
      let a;
      if (params.get('r')) {
        /* Compact link from the results email: ?r=q1_q2_q3_q4a.q4b */
        const [q1, q2, q3, q4 = ''] = params.get('r').split('_');
        a = { q1, q2, q3, q4: q4.split('.').filter((v) => valid('q4', v)) };
      } else {
        if (!params.get('q1')) return null;
        a = {
          q1: params.get('q1'),
          q2: params.get('q2'),
          q3: params.get('q3'),
          q4: (params.get('q4') || '').split(',').filter((v) => valid('q4', v)),
        };
      }
      if (!valid('q1', a.q1) || !valid('q2', a.q2) || !valid('q3', a.q3)) return null;
      return a;
    }

    routineUrl() {
      const a = this.answers();
      const params = new URLSearchParams();
      params.set('q1', a.q1 || '');
      params.set('q2', a.q2 || '');
      params.set('q3', a.q3 || '');
      if (a.q4 && a.q4.length) params.set('q4', a.q4.join(','));
      return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    }

    setupSave() {
      this.saveBox = this.querySelector('[data-quiz-save]');
      if (!this.saveBox) return;
      this.linkInput = this.saveBox.querySelector('[data-quiz-link]');
      this.saveStatus = this.saveBox.querySelector('[data-quiz-save-status]');
      this.emailLink = this.saveBox.querySelector('[data-quiz-email]');
      this.smsLink = this.saveBox.querySelector('[data-quiz-sms]');
      if (this.smsLink) this.smsLink.hidden = !window.matchMedia('(pointer: coarse)').matches;
      this.linkInput.addEventListener('focus', () => this.linkInput.select());
      this.saveBox.querySelector('[data-quiz-copy]').addEventListener('click', () => this.copyLink());
      this.setupShopifySignup();
      this.signup = this.saveBox.querySelector('[data-quiz-signup]');
      if (this.signup) {
        this.signupDone = this.saveBox.querySelector('[data-quiz-signup-done]');
        this.signupGuard = this.saveBox.querySelector('[data-quiz-signup-guard]');
        this.signupEmail = this.signup.querySelector('input[type="email"]');
        this.signupBtn = this.signup.querySelector('button[type="submit"]');
        this.signup.addEventListener('submit', (event) => this.submitSignup(event));
      }
    }

    /* Shopify customer form (double opt-in). Native submit reloads the page; answers come back from localStorage. */
    setupShopifySignup() {
      const box = this.saveBox.querySelector('[data-quiz-shopify-signup]');
      if (!box) return;
      const form = box.closest('form');
      const email = box.querySelector('input[type="email"]');
      const storageKey = `naturall-quiz-signup-${box.dataset.formId}`;
      let submittedHere = false;
      try { submittedHere = sessionStorage.getItem(storageKey) === '1'; sessionStorage.removeItem(storageKey); } catch (e) { /* ignore */ }

      if (submittedHere) {
        const done = box.querySelector('[data-quiz-signup-done]');
        const error = box.querySelector('[data-quiz-signup-error]');
        if (box.dataset.posted === 'true' && done) {
          box.querySelector('[data-quiz-signup-fields]').hidden = true;
          done.hidden = false;
        } else if (error) {
          error.hidden = false;
        }
        requestAnimationFrame(() => this.saveBox.scrollIntoView({ block: 'center' }));
      }

      form.addEventListener('submit', (event) => {
        if (!email.checkValidity() || !email.value.trim()) {
          event.preventDefault();
          this.saveStatus.textContent = box.dataset.labelInvalid;
          email.focus();
          return;
        }
        box.querySelector('[data-quiz-tags]').value = this.answerTags().join(',');
        track('quiz_email_submitted', { method: 'shopify_signup' });
        try { sessionStorage.setItem(storageKey, '1'); } catch (e) { /* ignore */ }
      });
    }

    answerTags() {
      const a = this.answers();
      const tags = ['quiz-taker'];
      ['q1', 'q2', 'q3'].forEach((q) => { if (a[q]) tags.push(`quiz-${q}-${a[q]}`); });
      (a.q4 || []).forEach((v) => tags.push(`quiz-q4-${v}`));
      /* One brand-new tag per submission (date-time + answers), so Flow's "Customer tags added" fires on every retake. */
      const d = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const stamp = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
      tags.push(`quiz-r-${stamp}__${a.q1 || ''}_${a.q2 || ''}_${a.q3 || ''}_${(a.q4 || []).join('.')}`);
      return tags;
    }

    submitSignup(event) {
      event.preventDefault();
      const d = this.signup.dataset;
      if (!this.signupEmail.checkValidity() || !this.signupEmail.value.trim()) {
        this.saveStatus.textContent = d.labelInvalid;
        this.signupEmail.focus();
        return;
      }
      this.signup.querySelector('[data-quiz-link-field]').value = this.routineUrl();
      this.saveStatus.textContent = '';
      this.signupBtn.disabled = true;
      fetch(this.signup.action, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(this.signup)
      })
        .then((res) => res.json())
        .then((data) => {
          this.signupBtn.disabled = false;
          if (data && data.status === 'quarantined' && data.url) {
            this.signupGuard.href = data.url;
            this.signupGuard.hidden = false;
            this.saveStatus.textContent = d.labelGuard;
            this.signupGuard.focus();
          } else if (data && data.status === 'success') {
            track('quiz_email_submitted', { method: 'signup' });
            this.signup.hidden = true;
            this.signupGuard.hidden = true;
            this.signupDone.hidden = false;
          } else {
            this.saveStatus.textContent = d.labelError;
          }
        })
        .catch(() => {
          this.signupBtn.disabled = false;
          this.signup.target = '_blank';
          HTMLFormElement.prototype.submit.call(this.signup);
        });
    }

    updateSave() {
      if (!this.saveBox) return;
      const url = this.routineUrl();
      const s = this.saveBox.dataset;
      const body = `${s.shareMessage ? `${s.shareMessage} ` : ''}${url}`;
      this.linkInput.value = url;
      this.saveStatus.textContent = '';
      if (this.emailLink) this.emailLink.href = `mailto:?subject=${encodeURIComponent(s.shareSubject || '')}&body=${encodeURIComponent(body)}`;
      if (this.smsLink) this.smsLink.href = `sms:?&body=${encodeURIComponent(body)}`;
      const linkField = this.signup && this.signup.querySelector('[data-quiz-link-field]');
      if (linkField) linkField.value = url;
    }

    copyLink() {
      const s = this.saveBox.dataset;
      const done = () => { this.saveStatus.textContent = s.labelCopied; };
      const fail = () => {
        this.linkInput.focus();
        this.linkInput.select();
        this.saveStatus.textContent = s.labelCopyFailed;
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(this.linkInput.value).then(done, fail);
      } else {
        fail();
      }
    }

    load() {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch (e) { return null; }
    }

    save(answers) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers })); } catch (e) { /* ignore */ }
    }

    answers() {
      const data = new FormData(this.form);
      return { q1: data.get('q1'), q2: data.get('q2'), q3: data.get('q3'), q4: data.getAll('q4') };
    }

    restore(a) {
      ['q1', 'q2', 'q3'].forEach((q) => {
        const input = this.form.querySelector(`input[name="${q}"][value="${a[q]}"]`);
        if (input) input.checked = true;
      });
      (a.q4 || []).forEach((v) => {
        const input = this.form.querySelector(`input[name="q4"][value="${v}"]`);
        if (input) input.checked = true;
      });
    }

    show(i, focus) {
      this.index = Math.max(0, Math.min(i, this.questions.length - 1));
      this.questions.forEach((q, n) => { q.hidden = n !== this.index; });
      this.form.hidden = false;
      this.intro.hidden = false;
      this.results.hidden = true;
      this.error.hidden = true;
      this.back.hidden = this.index === 0;
      const last = this.index === this.questions.length - 1;
      this.next.textContent = last ? this.next.dataset.labelFinish : this.next.dataset.labelNext;
      this.progress.textContent = (this.d.labelStep || '')
        .replace('[n]', this.index + 1)
        .replace('[total]', this.questions.length);
      if (focus) this.questions[this.index].querySelector('legend').focus();
    }

    goNext() {
      const q = this.questions[this.index];
      const name = q.dataset.question;
      if (q.dataset.optional !== 'true' && !this.form.querySelector(`input[name="${name}"]:checked`)) {
        this.error.textContent = this.d.labelRequired;
        this.error.hidden = false;
        const first = q.querySelector('input');
        if (first) first.focus();
        return;
      }
      if (this.index === 0 && !this.trackedStart) {
        this.trackedStart = true;
        track('quiz_started', { question: name });
      }
      if (this.index < this.questions.length - 1) {
        this.show(this.index + 1, true);
      } else {
        const answers = this.answers();
        this.save(answers);
        this.showResults(true);
        track('quiz_completed', { q1: answers.q1 || '', q2: answers.q2 || '', q3: answers.q3 || '', q4: (answers.q4 || []).join('.') });
        markCart('Completed quiz');
      }
    }

    restart() {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
      if (window.location.search.includes('q1=') || window.location.search.includes('r=')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
      this.form.reset();
      this.show(0, true);
    }

    /* Pick one recommendation per step from the answers. */
    plan() {
      const a = this.answers();
      const chosen = [a.q1, a.q2, a.q3 === 'both' ? 'both' : a.q3].filter(Boolean);
      if (a.q3 === 'both') chosen.push('oil', 'edges');
      const owned = a.q4 || [];
      return JOBS.map((job, i) => {
        const rec = this.recs.find((r) => r.job === job && r.answers.some((x) => chosen.includes(x)));
        const isOptional = job === 'oil' || job === 'edge';
        if (!rec && isOptional) return null;
        const step = { job, label: this.stepLabels[i] || job, browse: this.browseLinks[i] || '', have: owned.includes(job) };
        if (!rec) return step;
        let product = rec.product;
        let swapped = false;
        if (!product.available) {
          const alt = rec.alternatives.find((x) => x.available);
          if (alt) { product = alt; swapped = true; }
        }
        step.rec = rec;
        step.product = product;
        step.swapped = swapped;
        step.alternatives = [rec.product].concat(rec.alternatives).filter((x) => x.handle !== product.handle);
        return step;
      }).filter(Boolean);
    }

    price(p) {
      return `${p.varies ? `${esc(this.d.labelFrom)} ` : ''}${esc(p.price)}`;
    }

    showResults(focus) {
      this.steps = this.plan();
      this.cards.innerHTML = this.steps.map((s) => this.card(s)).join('');
      this.form.hidden = true;
      this.intro.hidden = true;
      this.results.hidden = false;
      const hasPlanner = !!document.querySelector('my-routine');
      this.querySelector('[data-quiz-review]').hidden = !hasPlanner;
      this.updateSave();
      if (focus) this.querySelector('[data-quiz-results-title]').focus();
    }

    card(s) {
      const scheme = s.rec ? s.rec.scheme : 'scheme-1';
      const head = `<p class="routine-quiz__card-step">${esc(s.label)}</p>`;
      if (s.have) {
        return `<li class="routine-quiz__card color-${esc(scheme)}">${head}<p class="routine-quiz__card-have">${esc(this.d.labelHave)}</p>${s.browse ? `<a class="link" href="${esc(s.browse)}">${esc(this.d.labelBrowse)}</a>` : ''}</li>`;
      }
      if (!s.product) {
        return `<li class="routine-quiz__card color-${esc(scheme)}">${head}${s.browse ? `<a class="link" href="${esc(s.browse)}">${esc(this.d.labelBrowse)}</a>` : ''}</li>`;
      }
      const p = s.product;
      const stock = !p.available
        ? `<p class="routine-quiz__stock">${esc(this.d.labelSoldOut)}</p>`
        : p.someSoldOut ? `<p class="routine-quiz__stock">${esc(this.d.labelSomeSoldOut)}</p>` : '';
      const img = p.image
        ? `<img class="routine-quiz__card-image" src="${esc(p.image)}" alt="" width="400" height="400" loading="lazy">`
        : '';
      const alts = s.alternatives.length
        ? `<div class="routine-quiz__alts"><p class="routine-quiz__alts-title">${esc(this.d.labelAlternatives)}</p><ul class="list-unstyled">${s.alternatives
            .map((x) => `<li><a class="link" href="${esc(x.url)}">${esc(x.title)}</a> · ${this.price(x)}${x.available ? '' : ` · ${esc(this.d.labelSoldOut)}`}</li>`)
            .join('')}</ul></div>`
        : '';
      return `<li class="routine-quiz__card color-${esc(scheme)}">
        ${head}
        <div class="routine-quiz__card-main">
          ${img}
          <div>
            <h3 class="routine-quiz__card-title"><a href="${esc(p.url)}">${esc(p.title)}</a></h3>
            <p class="routine-quiz__card-price">${this.price(p)}</p>
            ${stock}
            ${s.swapped ? `<p class="routine-quiz__swap">${esc(this.d.labelSwapped)}</p>` : ''}
            ${s.rec && s.rec.reason && !s.swapped ? `<p class="routine-quiz__reason">${esc(s.rec.reason)}</p>` : ''}
          </div>
        </div>
        ${alts}
        ${s.browse ? `<a class="link routine-quiz__browse" href="${esc(s.browse)}">${esc(this.d.labelBrowse)}</a>` : ''}
      </li>`;
    }

    review() {
      const selections = (this.steps || []).map((s) => {
        if (s.have) return { job: s.job, status: 'have' };
        if (s.product && s.product.available) return { job: s.job, handle: s.product.handle };
        return null;
      }).filter(Boolean);
      track('quiz_routine_review', { items: selections.length });
      markCart('Completed quiz + reviewed routine');
      document.dispatchEvent(new CustomEvent('my-routine:apply', { detail: { selections } }));
    }
  }

  customElements.define('routine-quiz', RoutineQuiz);
})();
